import React, { useState } from "react"
import { Box, LoadingOrErrorContainer } from "@mallardbay/lib-react-components"

import type { SpeciesMinimalFieldsFragment } from "~graphql/generated/graphql"
import {
    ListingType,
    SpeciesType,
    useAllSpeciesQuery,
} from "~graphql/generated/graphql"
import { useTabletMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"
import COPY from "~config/copy-constants"
import ShowMoreToggleButton from "~components/shared/show-more-toggle-button"
import { TextXLargeBold } from "~components/shared/todo-lib-react-components/text"

import { useSharedStyles } from "./use-shared-styles"
import CheckboxGridSection from "./checkbox-grid-section"

const COLLAPSED_COUNT_SMALL_SCREEN = 6
const COLLAPSED_COUNT_LARGE_SCREEN = 9

interface Props {
    readonly type: ListingType | undefined
    readonly selectedSpecies: string[]
    readonly setSelectedSpecies: (species: string[]) => void
}

export default function SpeciesSelector({
    type,
    selectedSpecies,
    setSelectedSpecies,
}: Props) {
    const styles = useStyles()
    const sharedStyles = useSharedStyles()

    const [isHuntingExpanded, setIsHuntingExpanded] = useState(false)
    const [isFishingExpanded, setIsFishingExpanded] = useState(false)

    const { data, loading: isLoading, error } = useAllSpeciesQuery()

    const { huntingSpecies, fishingSpecies } = useFilteredAndSortedSpecies({
        allSpecies: data?.allSpecies ?? [],
        isFishingExpanded,
        isHuntingExpanded,
    })
    const onSpeciesChange = ({ id, value }: { id: string; value: boolean }) => {
        if (value) {
            setSelectedSpecies([...selectedSpecies, id])
        } else {
            setSelectedSpecies(
                selectedSpecies.filter((speciesId) => speciesId !== id)
            )
        }
    }

    const {
        shouldShowHuntingSpecies,
        shouldShowFishingSpecies,
        shouldRenderSpacer,
        shouldRenderSelector,
    } = useShouldShowSections(type)

    return (
        <LoadingOrErrorContainer
            style={sharedStyles.wrapper}
            isLoading={isLoading}
            error={error}
            shouldRenderChildren={shouldRenderSelector}
        >
            <TextXLargeBold>{COPY.TARGET_SPECIES}</TextXLargeBold>
            <Box style={styles.gridWrapper}>
                {shouldShowHuntingSpecies && (
                    <>
                        <CheckboxGridSection
                            label={COPY.HUNTING}
                            items={huntingSpecies}
                            selectedItems={selectedSpecies}
                            onItemChange={onSpeciesChange}
                        />
                        <ShowMoreToggleButton
                            isShowingMore={isHuntingExpanded}
                            onToggle={() =>
                                setIsHuntingExpanded(!isHuntingExpanded)
                            }
                        />
                    </>
                )}
                {shouldRenderSpacer && <Box style={styles.spacer} />}
                {shouldShowFishingSpecies && (
                    <>
                        <CheckboxGridSection
                            label={COPY.FISHING}
                            items={fishingSpecies}
                            selectedItems={selectedSpecies}
                            onItemChange={onSpeciesChange}
                        />
                        <ShowMoreToggleButton
                            isShowingMore={isFishingExpanded}
                            onToggle={() =>
                                setIsFishingExpanded(!isFishingExpanded)
                            }
                        />
                    </>
                )}
            </Box>
        </LoadingOrErrorContainer>
    )
}

function useStyles() {
    return {
        gridWrapper: {
            paddingBottom: "24px",
        },
        spacer: {
            width: "100%",
            height: "24px",
        },
    }
}

function useFilteredAndSortedSpecies({
    allSpecies,
    isHuntingExpanded,
    isFishingExpanded,
}: {
    allSpecies: SpeciesMinimalFieldsFragment[]
    isHuntingExpanded: boolean
    isFishingExpanded: boolean
}) {
    const { isLargerThanTablet } = useTabletMediaQueries()

    const fishingSpecies = filterAndSortSpecies({
        allSpecies,
        type: SpeciesType.Fishing,
    })
    const huntingSpecies = filterAndSortSpecies({
        allSpecies,
        type: SpeciesType.Hunting,
    })

    const slicedHuntingSpecies = sliceSpeciesForCollapsedState({
        species: huntingSpecies,
        isExpanded: isHuntingExpanded,
        isLargerThanTablet,
    })
    const slicedFishingSpecies = sliceSpeciesForCollapsedState({
        species: fishingSpecies,
        isExpanded: isFishingExpanded,
        isLargerThanTablet,
    })

    return {
        fishingSpecies: slicedFishingSpecies,
        huntingSpecies: slicedHuntingSpecies,
    }
}

function useShouldShowSections(type: ListingType | null | undefined) {
    const shouldShowHuntingSpecies =
        !type || [ListingType.BlastCast, ListingType.Hunting].includes(type)
    const shouldShowFishingSpecies =
        !type || [ListingType.BlastCast, ListingType.Fishing].includes(type)

    const shouldRenderSpacer =
        shouldShowHuntingSpecies && shouldShowFishingSpecies
    const shouldRenderSelector =
        shouldShowHuntingSpecies || shouldShowFishingSpecies

    return {
        shouldShowHuntingSpecies,
        shouldShowFishingSpecies,
        shouldRenderSpacer,
        shouldRenderSelector,
    }
}

function filterAndSortSpecies({
    allSpecies,
    type,
}: {
    allSpecies: SpeciesMinimalFieldsFragment[]
    type: SpeciesType
}) {
    const allSpeciesCopy = [...allSpecies] // Sorting mutates array.

    return allSpeciesCopy
        .filter((species) => species.type === type)
        .sort(
            (speciesA, speciesB) =>
                speciesA.name.charCodeAt(0) - speciesB.name.charCodeAt(0)
        )
}

function sliceSpeciesForCollapsedState({
    species,
    isExpanded,
    isLargerThanTablet,
}: {
    species: SpeciesMinimalFieldsFragment[]
    isExpanded: boolean
    isLargerThanTablet: boolean
}) {
    if (isExpanded) return species

    const countToShow = isLargerThanTablet
        ? COLLAPSED_COUNT_LARGE_SCREEN
        : COLLAPSED_COUNT_SMALL_SCREEN

    return species.slice(0, countToShow)
}
